import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchActiveReverie, generateReverieImage, saveReverieReflection, fetchJobStatus } from '../api';
import { useI18n } from '../i18n';
import ImageLightbox from '../components/ImageLightbox';
import QuotaAlert from '../components/QuotaAlert';
import { useProgression } from '../context/ProgressionContext';
import GuardianVignette from '../components/guardians/GuardianVignette';
import api from '../api';
import './Ensonacion.css';

const MAX_REFLECTION_LENGTH = 1200;

function ReverieLoader({ title, detail }) {
  return (
    <div className="reverie-loader" role="status" aria-live="polite">
      <div className="reverie-loader-stack" aria-hidden="true">
        <div className="reverie-layer" />
        <div className="reverie-layer" />
        <div className="reverie-layer" />
      </div>
      <div className="reverie-loader-copy">
        <strong>{title}</strong>
        <span>{detail}</span>
      </div>
    </div>
  );
}

export default function Ensonacion() {
  const { t } = useI18n();
  const [image, setImage] = useState(null);
  const { progressionState, refreshState } = useProgression() || {};
  const imageRef = useRef(null);
  const [reflection, setReflection] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [backgroundJob, setBackgroundJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [retryContext, setRetryContext] = useState(null);
  const [retryAvailableAt, setRetryAvailableAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [showLightbox, setShowLightbox] = useState(false);
  const [showQuotaAlert, setShowQuotaAlert] = useState(false);
  const [showTutorialAdvance, setShowTutorialAdvance] = useState(false);
  const generatingRef = useRef(false);
  const mountedRef = useRef(false);
  const tRef = useRef(t);
  const initialGenerationStartedRef = useRef(false);
  const initialLoadStartedRef = useRef(false);

  useEffect(() => {
    imageRef.current = image;
  }, [image]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!retryAvailableAt) return undefined;
    const tick = () => setNow(Date.now());
    tick();
    const timer = setInterval(tick, 250);
    return () => clearInterval(timer);
  }, [retryAvailableAt]);

  const applyGeneratedImage = useCallback((nextImage) => {
    if (nextImage) {
      setImage(nextImage);
      setReflection('');
      if (progressionState?.stage === 'ENSONACION') {
        setShowTutorialAdvance(true);
      }
      return true;
    }
    return false;
  }, [progressionState?.stage]);

  useEffect(() => {
    if (!backgroundJob?.id || backgroundJob.status !== 'pending') return undefined;

    const intervalId = window.setInterval(async () => {
      try {
        const res = await fetchJobStatus(backgroundJob.id);
        const job = res.data;
        if (job.status === 'completed') {
          if (applyGeneratedImage(job.result?.image)) {
            setBackgroundJob(null);
            if (refreshState) refreshState();
          } else {
            setError(tRef.current('reverie.error'));
          }
          window.clearInterval(intervalId);
        } else if (job.status === 'error') {
          setError(tRef.current('mutantBox.delayedError'));
          setBackgroundJob(null);
          window.clearInterval(intervalId);
        }
      } catch (e) {
        console.error('Error polling Reverie job:', e);
      }
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [backgroundJob, applyGeneratedImage, refreshState]);

  const runGeneration = useCallback(async ({ initial = false } = {}) => {
    if (generatingRef.current) return;
    if (!initial && !imageRef.current) return;
    if (initial && initialGenerationStartedRef.current) return;
    if (initial) initialGenerationStartedRef.current = true;

    generatingRef.current = true;
    setGenerating(true);
    setError('');
    setStatus('');
    setRetryContext(null);
    setRetryAvailableAt(0);

    const previousImage = imageRef.current;
    let generationSucceeded = false;
    try {
      const res = await generateReverieImage();
      if (!mountedRef.current) return;

      if (res.status === 202 && res.data?.jobId) {
        setBackgroundJob({ id: res.data.jobId, status: 'pending' });
        generationSucceeded = true; // Count as success to avoid infinite initial generation loops
      } else {
        const nextImage = res?.data?.image || null;
        if (applyGeneratedImage(nextImage)) {
          generationSucceeded = true;
          if (refreshState) refreshState();
        } else {
          setError(tRef.current('reverie.error'));
          if (previousImage) setStatus(tRef.current('reverie.previousKept'));
        }
      }
    } catch (err) {
      if (!mountedRef.current) return;
      const data = err?.response?.data || {};

      if (err?.response?.status === 403 || data.quotaExceeded) {
        setShowQuotaAlert(true);
      }

      setError(data.error || tRef.current('reverie.error'));
      if (data.providerRateLimited) {
        setRetryContext(initial ? 'initial' : 'next');
        const retryAfterMs = Number(data.retryAfterMs);
        if (Number.isFinite(retryAfterMs) && retryAfterMs > 0) {
          setRetryAvailableAt(Date.now() + retryAfterMs);
        }
      }
      if (previousImage) setStatus(tRef.current('reverie.previousKept'));
    } finally {
      if (initial && !generationSucceeded) initialGenerationStartedRef.current = false;
      generatingRef.current = false;
      if (mountedRef.current) setGenerating(false);
    }
  }, [applyGeneratedImage, refreshState]);

  const handleNext = useCallback(() => {
    if (generatingRef.current || saving || !imageRef.current) return;
    runGeneration({ initial: false });
  }, [runGeneration, saving]);

  const handleRetry = useCallback(() => {
    if (generatingRef.current || saving) return;
    if (retryAvailableAt && Date.now() < retryAvailableAt) return;
    const retryAsNext = retryContext === 'next' && imageRef.current;
    runGeneration({ initial: !retryAsNext });
  }, [retryAvailableAt, retryContext, runGeneration, saving]);

  useEffect(() => {
    if (initialLoadStartedRef.current) return;
    initialLoadStartedRef.current = true;

    async function loadInitialImage() {
      setError('');
      setStatus('');
      setRetryContext(null);
      setRetryAvailableAt(0);
      setInitialLoading(true);
      try {
        const res = await fetchActiveReverie();
        if (!mountedRef.current) return;
        const activeImage = res?.data?.image || null;
        if (activeImage) {
          initialGenerationStartedRef.current = false;
          setImage(activeImage);
        } else {
          setInitialLoading(false);
          await runGeneration({ initial: true });
        }
      } catch (err) {
        if (!mountedRef.current) return;
        setError(err?.response?.data?.error || tRef.current('reverie.error'));
      } finally {
        if (mountedRef.current) setInitialLoading(false);
      }
    }

    loadInitialImage();
  }, [runGeneration]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!image?.id || saving || generating) return;

    setSaving(true);
    setError('');
    setStatus('');

    try {
      const safeReflection = reflection.slice(0, MAX_REFLECTION_LENGTH);
      const res = await saveReverieReflection(image.id, safeReflection);
      if (!mountedRef.current) return;
      setImage(res?.data?.image || image);
      setReflection('');
      setStatus(t('reverie.saved'));
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err?.response?.data?.error || t('reverie.saveError'));
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const showLoader = initialLoading || generating;
  const retryWaitMs = retryAvailableAt ? Math.max(0, retryAvailableAt - now) : 0;
  const retryDisabled = generating || saving || retryWaitMs > 0;
  const retryLabel = retryWaitMs > 0
    ? `${t('reverie.retry')} (${Math.ceil(retryWaitMs / 1000)}s)`
    : t('reverie.retry');

  return (
    <main className="reverie-page">
      {showTutorialAdvance && (
        <GuardianVignette
          buttonText={t('guardian.stages.continueBtn')}
          message={t('guardian.stages.ensonacionAdvanceMsg')}
          onContinue={() => {
            api.post('/progression/advance', { nextStage: 'TAREAS' }).then(() => refreshState?.());
            setShowTutorialAdvance(false);
          }}
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        {progressionState?.stage === 'ENSONACION' && (
          <div className="cyber-hud-counter" style={{ color: 'var(--cyber-magenta)', letterSpacing: '2px', marginBottom: '20px', fontFamily: 'Orbitron, sans-serif', textShadow: '0 0 10px var(--cyber-magenta)', fontSize: '1.2rem', textAlign: 'center', zIndex: 2 }}>
            SINTETIZANDO PROYECCIONES...<br/>
            Nodos Sincronizados: {progressionState.stage_interactions || 0}/3
          </div>
        )}
        <section className="reverie-shell" aria-labelledby="reverie-title">
        <h1 id="reverie-title" className="reverie-title">{t('reverie.title')}</h1>

        <div className="reverie-image-stage">
          {showLoader ? (
            <ReverieLoader
              title={backgroundJob ? t('mutantBox.delayedGeneration') : t('reverie.loading')}
              detail={backgroundJob ? t('mutantBox.keepExploring') : t('reverie.loadingDetail')}
            />
          ) : image?.imageData ? (
            <img
              className="reverie-image"
              src={image.imageData}
              alt={t('reverie.imageAlt')}
              onClick={() => setShowLightbox(true)}
              style={{ cursor: 'zoom-in' }}
            />
          ) : (
            <div className="reverie-empty">{t('reverie.empty')}</div>
          )}
        </div>

        {image && !showLoader && <form className="reverie-form" onSubmit={handleSubmit}>
          <textarea
            className="reverie-input"
            value={reflection}
            onChange={(event) => {
              setReflection(event.target.value.slice(0, MAX_REFLECTION_LENGTH));
              setStatus('');
            }}
            placeholder={t('reverie.prompt')}
            aria-label={t('reverie.prompt')}
            maxLength={MAX_REFLECTION_LENGTH}
            disabled={generating || saving || !image}
          />
          <button
            type="submit"
            className="reverie-button primary"
            disabled={generating || saving || !image}
          >
            {saving ? t('common.saving') : t('reverie.send')}
          </button>
          <button
            type="button"
            className="reverie-button"
            onClick={handleNext}
            disabled={generating || saving || !image}
          >
            {t('reverie.next')}
          </button>
        </form>}

        {backgroundJob && (
          <div className="reverie-status" style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <p>{t('mutantBox.delayedGeneration')}</p>
            <button
              type="button"
              className="reverie-button"
              onClick={() => setBackgroundJob(null)}
            >
              {t('mutantBox.keepExploring')}
            </button>
          </div>
        )}

        {status && <p className="reverie-status" role="status">{status}</p>}
        {error && <p className="reverie-error" role="alert">{error}</p>}
        {!showLoader && error && (retryContext || !image) && (
          <button className="reverie-retry" type="button" onClick={handleRetry} disabled={retryDisabled}>
            {retryLabel}
          </button>
        )}
        </section>
      </div>

      {showLightbox && image?.imageData && (
        <ImageLightbox
          src={image.imageData}
          alt={t('reverie.imageAlt')}
          onClose={() => setShowLightbox(false)}
        />
      )}

      <QuotaAlert
        isOpen={showQuotaAlert}
        onClose={() => setShowQuotaAlert(false)}
      />
    </main>
  );
}
